import React, { memo } from 'react'

import * as c from '../../../common'
import {
  ESR_SHORT_NAME,
  esrPartyInfo,
  usesHomeImprovementAgreement,
} from '../../../common/helpers/esrContractCompany'
import {
  REMOTE_SEND_RULES,
  SIGNING_SECTION_LABELS,
  SigningSection,
  primaryContractSent,
  remoteSendBlock,
  remoteSigningRecipient,
  scopeProducts,
  sectionChoice,
  sectionSent,
} from '../../../common/helpers/signingSections'
import { jobSummaryVisible } from '../../../common/helpers/jobSummaryScope'
import { legacyAgreementSigned } from '../../../common/helpers/legacySignedStatus'
import {
  appliedMonthlyPayment,
  effectiveMonthlyPayment,
  hasPhase1Signature,
  isSigningFlowV2,
  monitoringChoiceOffered,
  monthlyPersistExtra,
  paymentSignReady,
  paymentSignatureExtras,
  sanitizeMonitoringChoice,
  signingFlowStamp,
} from '../../../common/helpers/signingFlowV2'
import { isCashPayment } from '../../../common/helpers/paymentDisplay'
import * as r from '../../../react-utils'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Download from '@mui/icons-material/Download'
import Email from '@mui/icons-material/Email'
import Fontisto from 'react-native-vector-icons/Fontisto'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Receipt from '@mui/icons-material/Receipt'
import SettingsIcon from '@mui/icons-material/Settings'
import { useParams, useSearchParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'

import * as cp from '../../comps'
import * as cpm from '../../../app/components'
import * as gs from '../../../app/gStyles'
import FaqPage from '../../comps/FaqPage'
import FinancingPage from '../../comps/FinancingPage/FinancingPage'
import MainPageReport from '../../comps/MainPageReport'
import MediaImagePages from '../../comps/MediaImagePages'
import TestimonialVideoPage from '../../comps/TestimonialVideoPage/TestimonialVideoPage'
import Modal from '../../comps/Modal'
import PageReport, { pageWidth } from '../../comps/PageReport'
import ReleaseLiabilityPage from '../../comps/ReleaseLiabilityPage'
import RoofClaimingContractPages from '../../comps/RoofClaimingContractPages'
import {
  roofClaimVisible,
  roofClaimStamp,
  linkDelivery,
  roofClaimLegacyStatus,
  roofSectionDisplay,
  signingDeliveryField,
} from './roofClaimFlow'
import Signature from '../../comps/Signature'
import downloadReportAsPdf, {
  generatePdfBytes,
} from '../../helpers/downloadReportAsPdf'
import { EMAIL_PDF_PROFILE } from '../../helpers/pdfRenderProfile'
import sendContractByEmail from '../../helpers/sendContractByEmail'
import {
  AUTO_EMAIL_SIGNED_CONTRACT_ENABLED,
  AUTO_EMAIL_SIGNED_CONTRACT_ERROR,
  pdfBytesToBase64,
} from '../../helpers/autoEmailSignedContract'
import GuidedSigning from './GuidedSigning'
import { assertEmailAttachmentSize } from '../../helpers/emailAttachmentSize'
import { deliverOnce } from '../../helpers/guidedEmailDelivery'
import { guidedEmailStore } from '../../helpers/guidedEmailStore'
import {
  SELECT_COMPANY_FIRST,
  canAdoptInto,
  signingSteps,
  canFinish,
  contractCompanyMissing,
  renderedSigningSteps,
  stepsOfPart,
} from './guidedSigningFlow'
import {
  auth,
  currentIdToken,
  readSigningSections,
  useIsSigningSession,
} from '../../fire' // ??
import { contentUser } from '../../helpers/ContentUser'
import { useGetMediaUrls } from '../../hooks/useGetMediaUrls'

import AlarmVerifiedResponsePage from './AlarmVerifiedResponsePage'
import CertOfCompletion from './CertOfCompletion'
import CumComp from './CumComp'
import FinancingCalculator from './FinancingCalculator'
import FirstElectricContract from './FirstElectricContract'
import LoanOptions from './LoanOptions'
import MSElectricCertOfCompletion from './MSElectricCertOfCompletion'
import MSElectricContract from './MSElectricContract'
import type { ContractPartyInfo } from './MSElectricContract'
import NirvanaContract from './NirvanaContract'
import OtherProviders from './OtherProviders/OtherProviders'
import ProductLineup from './ProductLineup/ProductLineup'
import ProductVideos from './ProductVideos/ProductVideos'
import { roofClaimAutofillPatch } from '../../../common/helpers/roofClaimAutofill'
import {
  inReportScope,
  PRODUCTS_LOAD_TIMEOUT_MS,
  ProductsConfirmation,
  reportProductsResolved,
} from '../../../common/helpers/reportProductScope'
import { smartHomeVideosVisible } from '../../../common/helpers/smartHomeVideosScope'
import SalesSlick from './SalesSlick/SalesSlick'
import Summary from './Summary'
import WildcatContract from './WildcatContract'
import { signingCardSlotStyle } from './signingCardSlot'
import { homeCamContract, mseContract } from '../../constants'
//@ts-ignore
import styles from './CustomerReport.module.css'

/**
 * Caballero's Electric. Valores tomados de `Misc/env/contractCompanies/CE`
 * (leido 2026-09-09). OJO con ese registro: NO tiene direccion — sus campos
 * `address1`/`address2` llevan dentro el website y el bid limit, que es como
 * los imprimia el contrato viejo. Aqui van a su sitio real de la caja de
 * CONTRACTOR'S INFORMATION, y las dos lineas de direccion simplemente no se
 * pintan, en vez de imprimir "Website: ..." donde deberia ir una calle.
 */
const CE_COMPANY: ContractPartyInfo = {
  bidLimit: '$150,000',
  email: 'info@caballeroselectric.com',
  license: '0088081',
  name: "Caballero's Electric LLC",
  phone: '702-420-2425',
  website: 'www.caballeroselectric.com',
}

const reportTheme = createTheme({
  palette: {
    mode: 'light',
  },
  breakpoints: {
    values: r.MUIBreakpoints,
  },
})

/** Read-only: tells a legacy empty `products` from one still loading (V45). */
const useProductsResolved = (customerID: string): boolean => {
  const [confirmation, setConfirmation] = React.useState<ProductsConfirmation>({
    customerID: '',
    status: 'loading',
    value: '',
  })
  const [timedOutID, setTimedOutID] = React.useState('')
  React.useEffect(() => {
    if (!customerID) return
    let active = true
    let unsubscribe: (() => void) | undefined
    const settle = (status: ProductsConfirmation['status'], value = '') => {
      if (active) setConfirmation({ customerID, status, value })
    }
    try {
      unsubscribe = c.subToCustomerField(
        customerID,
        'products',
        (value) => settle('ready', value),
        () => settle('error'),
      )
    } catch {
      settle('error')
    }
    const timer = setTimeout(() => {
      if (active) setTimedOutID(customerID)
    }, PRODUCTS_LOAD_TIMEOUT_MS)
    return () => {
      active = false
      clearTimeout(timer)
      unsubscribe?.()
    }
  }, [customerID])
  return reportProductsResolved(
    customerID,
    confirmation,
    timedOutID === customerID,
  )
}

export default memo(function CustomerReport() {
  // c.debug('<CustomerReport />()')
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `No customerID inside <CustomerReport />. Contact dev dpt.`,
    )
  }

  const [searchParams] = useSearchParams()
  // Public token-signing flow (see SignContractPage): customer should only
  // be able to view, sign, and download — never edit contract values.
  // Derived from BOTH the URL param and the signed-in account: the param
  // alone was bypassable by deleting it from the URL bar (V50).
  const isSigningSession = useIsSigningSession()
  const readOnly = searchParams.get('mode') === 'sign' || isSigningSession

  const t = r.useTheme()

  //#region global
  const {
    currProducts: dealProducts,
    toggleHIL,
    toggleRC,
    toggleSecurity,
    toggleSolar,
  } = r.useProducts(customerID)
  // Remote link (13411491362): the sections the rep sent. null = in person, or
  // a link from before sections: the full report. Scopes the page only, see
  // signingSections.ts: this is not a barrier against devtools.
  const signingSections = React.useMemo(
    () => (readOnly ? readSigningSections() : null),
    [readOnly],
  )
  const linkOpenedAt = React.useRef(Date.now()).current
  const currProducts = scopeProducts(dealProducts, signingSections)
  // Jaron, Oct 30 (13433630544): "Smart Home shows up for a second". Empty
  // products means "everything" on a legacy deal, and also "not loaded yet".
  const productsResolved = useProductsResolved(customerID)
  const totalCostCompany = r.useTotalCost('company', customerID, true)
  // Global switch for the "Other providers" page (Misc/env); a missing key
  // means on.
  const comparisonEnabled = c.comparisonFeatureEnabled(
    r.useEnv(c.OTHER_PROVIDER_COMPARISON_ENV_KEY),
  )
  //#endregion global

  const [invoiceModal, setInvoiceModal] = React.useState(false)

  // const [lang, setLang] = React.useState<common.Lang>('ENG')

  // const handleLang = React.useCallback(() => {
  //   setLang((current) => (current === 'ENG' ? 'ES' : 'ENG'))
  // }, [setLang])

  const [signatureModalOpen, setSignatureModalOpen] = React.useState(false)
  const [companySignatureModalOpen, setCompanySignatureModalOpen] =
    React.useState(false)
  const [repSignatureModalOpen, setRepSignatureModalOpen] =
    React.useState(false)

  const [hilCompletionSigning, , toggleHILCompletionSigning] = r.useBool(false)

  const [secContractSigningCompanyOpen, , secContractSigningCompanyToggle] =
    r.useBool(false)
  const [secContractSigningCustomerOpen, , secContractSigningCustomerToggle] =
    r.useBool(false)
  const [
    secContractCancelSigningCustomerOpen,
    ,
    secContractCancelSigningCustomerToggle,
  ] = r.useBool(false)

  const [secCompletionSigning, , toggleSecCompletionSigning] = r.useBool(false)

  const [homeImprovementSignOwner1Open, , homeImprovementSignOwner1Toggle] =
    r.useBool(false)
  const [homeImprovementSignOwner2Open, , homeImprovementSignOwner2Toggle] =
    r.useBool(false)
  const [homeImprovementSignCompanyOpen, , homeImprovementSignCompanyToggle] =
    r.useBool(false)
  const [
    homeImprovementCancelSignOwner1Open,
    ,
    homeImprovementCancelSignOwner1Toggle,
  ] = r.useBool(false)
  const [
    homeImprovementCancelSignOwner2Open,
    ,
    homeImprovementCancelSignOwner2Toggle,
  ] = r.useBool(false)
  // MSE Scope of Work confirmation boxes (ticket 12915736128) — a signature
  // between the equipment list and the TOTAL, and an initials box next to the
  // TOTAL. Filled by the customer during the public signing flow (Luis:
  // "Cliente es"), same as Owner 1 — so, like it, not `readOnly`-gated.
  const [
    homeImprovementConfirmEquipmentOpen,
    ,
    homeImprovementConfirmEquipmentToggle,
  ] = r.useBool(false)
  const [
    homeImprovementConfirmTotalInitialsOpen,
    ,
    homeImprovementConfirmTotalInitialsToggle,
  ] = r.useBool(false)
  // Signing v2 (phase B): Job Summary monthly payment, NOTES and M+S / CE
  // Financing Information. Customer signatures, not `readOnly`-gated.
  const [jobSummaryPaymentSignOpen, , jobSummaryPaymentSignToggle] =
    r.useBool(false)
  const [jobSummaryNotesSignOpen, , jobSummaryNotesSignToggle] =
    r.useBool(false)
  const [jobSummaryMonthlySignOpen, , jobSummaryMonthlySignToggle] =
    r.useBool(false)
  const [financingInfoSignOpen, , financingInfoSignToggle] = r.useBool(false)

  const [roofClaimClientSigning, , toggleRoofClaimClientSigning] =
    r.useBool(false)
  const [roofClaimRepSigning, , toggleRoofClaimRepSigning] = r.useBool(false)

  const [solarBill, setSolarBill] = React.useState(0)
  const [powerBill, setPowerBill] = React.useState(0)
  const [alarmBill, setAlarmBill] = React.useState(0)

  // NORMAL CLOSERS SOLAR SECTION STATES
  const [altSolarBill, setAltSolarBill] = React.useState(0)
  const [altPowerBill, setAltPowerBill] = React.useState(0)
  const [altAlarmBill, setAltAlarmBill] = React.useState(0)

  const handleSolarBill = React.useCallback(
    (value: string) => {
      setSolarBill(c.parseFloating(value))
    },
    [setSolarBill],
  )

  const handlePowerBill = React.useCallback(
    (value: string) => {
      setPowerBill(c.parseFloating(value))
    },
    [setPowerBill],
  )

  const handleAlarmBill = React.useCallback(
    (value: string) => {
      setAlarmBill(c.parseFloating(value))
    },
    [setAlarmBill],
  )

  // NORMAL CLOSERS HANDLE STATES FUNCTIONS
  const handleAltSolarBill = React.useCallback(
    (value: string) => {
      setAltSolarBill(c.parseFloating(value))
    },
    [setAltSolarBill],
  )

  const handleAltPowerBill = React.useCallback(
    (value: string) => {
      setAltPowerBill(c.parseFloating(value))
    },
    [setAltPowerBill],
  )

  const handleAltAlarmBill = React.useCallback(
    (value: string) => {
      setAltAlarmBill(c.parseFloating(value))
    },
    [setAltAlarmBill],
  )

  // const navigate = useNavigate()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [includeCoverAndSummary, , toggleCoverAndSummary] = r.useBool(false)
  const [includeCertOfCompletion, , toggleCertOfCompletion] = r.useBool(false)
  // Signing v2: the PDF emailed at Finish carries the cover, Job Summary and
  // NOTES when the customer signed any of them, whatever the menu toggle says.
  const [forceSummaryInPdf, setForceSummaryInPdf] = React.useState(false)
  const summaryNoPDF = !(
    includeCoverAndSummary ||
    forceSummaryInPdf ||
    !!signingSections?.includes('cover')
  )
  const certificateNoPDF =
    !includeCertOfCompletion && !signingSections?.includes('certificate')
  // Report menu sections (13411491362). Local view options, like cover and
  // certificate: toggleRC would write products, and could not hide a claim
  // shown by its evidence fields anyway (V92).
  const [includeRoofClaim, , toggleIncludeRoofClaim] = r.useBool(true)
  const [includeAuthorization, , toggleIncludeAuthorization] = r.useBool(true)
  const [roofSigningStarted, setRoofSigningStarted] = React.useState(false)
  const [forceRoofInPdf, setForceRoofInPdf] = React.useState(false)
  const roofClaimDisplay = roofSectionDisplay(
    includeRoofClaim,
    roofSigningStarted,
    forceRoofInPdf,
  )
  const authorizationDisplay = roofSectionDisplay(
    includeAuthorization,
    roofSigningStarted,
    forceRoofInPdf,
  )
  const [pdfLoading, setPdfLoading] = React.useState(false)
  const [pdfError, setPdfError] = React.useState<string | null>(null)
  const [emailLoading, setEmailLoading] = React.useState(false)
  const [emailError, setEmailError] = React.useState<string | null>(null)
  const [emailSent, setEmailSent] = React.useState(false)
  const signingRoot = React.useRef<HTMLDivElement>(null)
  const openMenu = Boolean(anchorEl)
  const invoiceUsers = c.useSelector(c.selectInvoiceUsers)

  const openSettings = React.useCallback(
    (event: any) => {
      setAnchorEl(event.currentTarget)
      // document.body.classList.add('remove-scrollbar')
    },
    [setAnchorEl],
  )

  const handleClose = React.useCallback(() => {
    setAnchorEl(null)
  }, [setAnchorEl])

  const openInvoiceModal = React.useCallback(() => {
    handleClose()
    setInvoiceModal(true)
    document.body.classList.add('remove-scrollbar')
  }, [handleClose, setInvoiceModal])

  React.useEffect(() => void c.dispatch(c.efficiencyPricesAction()), [])

  // TODO: Customer edit subscription still on when this happens?
  React.useEffect((): c.VoidFn => {
    c.dispatch(
      c.subToSingleCustomer({
        customerID: customerID,
      }),
    )
    return () => {
      c.dispatch(
        c.unSubFromSingleCustomer({
          customerID,
        }),
      )
    }
  }, [customerID])

  const customer = c.useSelector(
    (_): c.Customer =>
      _.customers.data[customerID] ||
      c.createEmptyCustomer({
        firebaseKey: customerID,
      }),
  )

  const {
    contractCompaniesAllStates,
    contractCompanyShortName,
    shouldOfferCompanySelector,
  } = r.useContractCompany(customerID)

  // ESR LLC (13376550851): details from Misc/env/contractCompanies/ESR, with
  // marked placeholders for anything Jaron has not given us yet.
  const esrCompany = React.useMemo(
    (): ContractPartyInfo =>
      esrPartyInfo(contractCompaniesAllStates[ESR_SHORT_NAME]),
    [contractCompaniesAllStates],
  )

  const {
    c_signature_date,
    c_signature,
    customerAddress,
    customerName,
    homeRep,
    report_company_signature_date,
    report_company_signature,
    solarCompany,
    solarRep,
    sort_key,
  } = customer

  const state = c.getCompanyState(solarCompany)
  // Signing v2: new steps only for a customer on v2 or not signed yet (V67).
  const signingV2 = c.IS_MAIN && isSigningFlowV2(customer)
  const roofOnReport = roofClaimVisible(c.IS_MAIN, dealProducts, customer)
  const showRoofClaim =
    roofOnReport && sectionSent(signingSections, 'roof_claim')
  const showAuthorization =
    roofOnReport && sectionSent(signingSections, 'authorization')
  const contractSent = primaryContractSent(signingSections)
  const coverShown =
    !signingSections || contractSent || signingSections.includes('cover')
  const primaryLegacySigned = legacyAgreementSigned(
    customer,
    contractCompanyShortName,
  )
  const legacySigned = showRoofClaim
    ? roofClaimLegacyStatus(
        customer,
        primaryLegacySigned,
        signingSteps(
          contractCompanyShortName,
          customer,
          signingV2,
          c.IS_MAIN &&
            (currProducts.includes('Security') || currProducts.length === 0),
        ).length > 0,
      )
    : contractSent && primaryLegacySigned
  const deliveryField = signingDeliveryField(roofOnReport)
  // Roof Claim boxes the rep emptied on purpose stay empty, on screen and in
  // what the first signature stores (13433630443).
  const [roofAutofillCleared, setRoofAutofillCleared] = React.useState<
    ReadonlySet<string>
  >(() => new Set())
  const handleRoofAutofillCleared = React.useCallback(
    (field: string, cleared: boolean) =>
      setRoofAutofillCleared((prev) => {
        if (prev.has(field) === cleared) return prev
        const next = new Set(prev)
        if (cleared) next.add(field)
        else next.delete(field)
        return next
      }),
    [],
  )
  const guidedSteps = signingSteps(
    (primaryLegacySigned && showRoofClaim) || !contractSent
      ? undefined
      : contractCompanyShortName,
    customer,
    signingV2,
    // Same gate as the FE / HC / Wildcat / OTHER contract below.
    c.IS_MAIN &&
      (currProducts.includes('Security') || currProducts.length === 0),
    showRoofClaim,
    !!signingSections &&
      showAuthorization &&
      REMOTE_SEND_RULES.authorizationRequiredAtFinish,
  )
  const delivery = signingSections
    ? linkDelivery(customer[deliveryField] || '', linkOpenedAt)
    : { resend: false, shown: customer[deliveryField] || '' }
  // Roof-Claim-only report: no Job Summary (empty table, $0.00 and the alarm
  // monitoring line had nothing to describe). Rule and its exceptions in
  // common/helpers/jobSummaryScope.ts.
  const showJobSummary = jobSummaryVisible({
    jobSummarySigned: !!(
      customer.job_summary_payment_sign ||
      customer.job_summary_monthly_sign ||
      customer.job_summary_notes_sign
    ),
    products: currProducts,
    reportSigningSteps: guidedSteps.some(({ part }) => part === 'report'),
    roofClaimShown: showRoofClaim,
  })
  // Monthly figure the Job Summary payment signature freezes: the one the
  // rep typed from the lender, else the applied loan's (13290658389). When
  // nothing was typed, the signed figure is also stored in the contract's
  // Monthly box so the agreement signed next shows the same amount.
  const { appliedLoan, paymentData } = r.usePayment(customerID)
  const appliedMonthly = isCashPayment(paymentData)
    ? 0
    : effectiveMonthlyPayment(
        customer.home_improvement_monthly_payment,
        appliedMonthlyPayment(appliedLoan),
      )
  const paymentSignExtras = React.useCallback(
    (): c.PartialCustomer => ({
      ...paymentSignatureExtras({
        company: contractCompanyShortName,
        customer,
        monthlyPayment: appliedMonthly,
      }),
      ...monthlyPersistExtra(customer, appliedMonthly),
    }),
    [appliedMonthly, contractCompanyShortName, customer],
  )
  // Jaron, Oct 20 (13342207732): the Job Summary signature buttons stay out
  // until the rep presses Start (or Sign all) on the Part 1 card, then they
  // populate. Not for the customer's own signing link, a PDF, or a report
  // with nothing left to sign in Part 1.
  const [reportSigningStarted, setReportSigningStarted] = React.useState(false)
  const hideJobSummarySigns =
    signingV2 &&
    !readOnly &&
    !reportSigningStarted &&
    stepsOfPart(guidedSteps, 'report').some((step) => !step.signed)
  const markSigningV2 = React.useCallback(() => {
    setRoofSigningStarted(true)
    if (!c.IS_MAIN) return
    const stamp = {
      ...signingFlowStamp(customer),
      ...(showRoofClaim ? roofClaimStamp(customer) : {}),
    }
    if (Object.keys(stamp).length) c.updateCustomer(customerID, stamp)
  }, [customer, customerID, showRoofClaim])
  // Only where a company can actually be picked: pre-2025 customers and
  // white-label tenants have no selector, so the gate would just strand them.
  const companyMissing =
    c.IS_MAIN &&
    shouldOfferCompanySelector &&
    contractCompanyMissing(contractCompanyShortName)
  const latestGuidedSteps = React.useRef(guidedSteps)
  latestGuidedSteps.current = guidedSteps
  // Guided "Sign all at once": the signature the customer just drew is placed
  // on the other eligible steps, each with its own signing timestamp field.
  const adoptGuidedSignature = React.useCallback(
    (source: string, targets: string[]) => {
      if (!canAdoptInto(source)) return
      const image = (customer as unknown as Record<string, unknown>)[source]
      // The monthly payment signature records the monitoring choice: never
      // place it before one is picked (where one is offered).
      const placeable = targets.filter(
        (field) =>
          canAdoptInto(field) &&
          latestGuidedSteps.current.some(
            (step) => step.field === field && !step.signed,
          ) &&
          (field !== 'job_summary_payment_sign' ||
            paymentSignReady(
              sanitizeMonitoringChoice(customer.job_summary_monitoring_choice),
              monitoringChoiceOffered(contractCompanyShortName, customer),
            )),
      )
      if (typeof image !== 'string' || !image || !placeable.length) return
      const now = c.normalizeTimestampToMs(Date.now())
      const data: Record<string, unknown> = {
        ...signingFlowStamp(customer),
        ...(placeable.includes('roof_claiming_client_sign')
          ? {
              ...roofClaimStamp(customer),
              ...roofClaimAutofillPatch(customer, roofAutofillCleared),
            }
          : {}),
        ...(placeable.includes('job_summary_payment_sign')
          ? paymentSignExtras()
          : {}),
      }
      for (const field of placeable) {
        data[field] = image
        data[field + '_date'] = now
      }
      c.updateCustomer(customerID, data as c.PartialCustomer)
    },
    [
      contractCompanyShortName,
      customer,
      customerID,
      paymentSignExtras,
      roofAutofillCleared,
    ],
  )
  const sendFinishedContract = async (
    confirmedEmail: string,
    resend = false,
  ) => {
    if (
      legacySigned ||
      !AUTO_EMAIL_SIGNED_CONTRACT_ENABLED ||
      !canFinish(
        renderedSigningSteps(latestGuidedSteps.current, signingRoot.current),
      )
    )
      throw new Error(AUTO_EMAIL_SIGNED_CONTRACT_ERROR)
    // Match the manual PDF preparation: hide editing controls and media galleries.
    setPdfLoading(true)
    // Signing v2 (approved decision 2): signed Job Summary / NOTES go in the
    // emailed PDF, with the cover, even when the menu toggle is off.
    if (hasPhase1Signature(customer)) setForceSummaryInPdf(true)
    // The signed packet is complete whatever the menu's section options say.
    setForceRoofInPdf(true)
    let result: Awaited<ReturnType<typeof generatePdfBytes>>
    try {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      )
      // Lighter email profile (roof claim photo pages overflowed 20mb);
      // manual Download PDF keeps DOWNLOAD_PDF_PROFILE.
      result = await generatePdfBytes(customerName, {
        renderer: 'canvas',
        profile: EMAIL_PDF_PROFILE,
      })
    } finally {
      setPdfLoading(false)
      setForceSummaryInPdf(false)
      setForceRoofInPdf(false)
    }
    if (
      !result ||
      !result.bytes.length ||
      !canFinish(
        renderedSigningSteps(latestGuidedSteps.current, signingRoot.current),
      )
    )
      throw new Error(AUTO_EMAIL_SIGNED_CONTRACT_ERROR)
    const pdfBase64 = pdfBytesToBase64(result.bytes)
    // Size guard BEFORE the receipt claim: oversized = definite failure,
    // receipt untouched, Finish/Resend stays available.
    assertEmailAttachmentSize(pdfBase64)
    const { fileName } = result
    await deliverOnce(
      guidedEmailStore(customerID, deliveryField),
      confirmedEmail,
      resend || delivery.resend,
      async () =>
        sendContractByEmail(
          customerName,
          confirmedEmail,
          customerID,
          { fileName, pdfBase64 },
          { idToken: await currentIdToken() },
        ),
    )
  }

  const handleDownloadPdf = React.useCallback(async () => {
    setPdfError(null)
    // Every section unchecked: say so instead of silently downloading nothing.
    if (!document.querySelector('[data-pdf-page]')) {
      setPdfError(
        'Nothing is selected for the PDF. Check at least one section.',
      )
      return
    }
    setPdfLoading(true)
    await new Promise((r) => requestAnimationFrame(r))
    try {
      await downloadReportAsPdf(customerName)
    } catch (e) {
      setPdfError(c.processErr(e))
    } finally {
      setPdfLoading(false)
    }
  }, [customerName])

  // Remote link (13411491362): the rep picks what goes, without touching the
  // deal's products. Preselected from the menu; rules in signingSections.ts.
  const recipient = remoteSigningRecipient(customer)
  const sendChoice = sectionChoice({
    agreementAlwaysPrints: usesHomeImprovementAgreement(
      contractCompanyShortName,
    ),
    includeAuthorization,
    includeCertificate: includeCertOfCompletion,
    includeCover: includeCoverAndSummary,
    includeRoofClaim,
    products: dealProducts,
    roofOnReport,
  })
  const [sendSections, setSendSections] = React.useState<
    SigningSection[] | null
  >(null)
  const sendBlock = sendSections
    ? remoteSendBlock(customer, sendSections)
    : null
  const closeSendDialog = () => setSendSections(null)
  const toggleSendSection = (section: SigningSection) =>
    setSendSections((current) =>
      !current
        ? current
        : current.includes(section)
        ? current.filter((s) => s !== section)
        : [...current, section],
    )
  const handleSendEmail = async () => {
    // The customer cannot pick the company in readOnly (B55): no link first.
    if (companyMissing || sendBlock || !recipient) return
    const sections = c.IS_MAIN ? sendSections || undefined : undefined
    setSendSections(null)
    setEmailError(null)
    setEmailSent(false)
    setEmailLoading(true)
    await new Promise((r) => requestAnimationFrame(r))
    try {
      await sendContractByEmail(
        customerName,
        recipient,
        customerID,
        undefined,
        { idToken: await currentIdToken(), sections },
      )
      setEmailSent(true)
      toast.success('Signing link sent to ' + recipient, { autoClose: 6000 })
    } catch (e) {
      setEmailError(c.processErr(e))
    } finally {
      setEmailLoading(false)
    }
  }
  const openSendDialog = () => {
    handleClose()
    if (!recipient || companyMissing) return
    if (c.IS_MAIN) setSendSections(sendChoice.selected)
    else void handleSendEmail()
  }

  const dismissPdfError = React.useCallback(() => {
    setPdfError(null)
  }, [])

  const dismissEmailError = React.useCallback(() => {
    setEmailError(null)
  }, [])

  const isInvoiceUser = React.useMemo(() => {
    const authUser = auth?.currentUser?.email
    if (!authUser) return false

    const _authUser = authUser.replace('.com', '')

    if (!invoiceUsers) return false

    const invoiceUser = invoiceUsers[_authUser]

    if (invoiceUser && (solarCompany === 'CPS' || solarCompany === 'NPS'))
      return true
    return false
  }, [invoiceUsers, solarCompany])

  const selectAllCustomerMedia = React.useMemo(
    () => c.makeSelectAllCustomerMedia(),
    [],
  )
  const selectAllCustomerMediaArgs = React.useMemo(
    (): c.SelectAllCustomerMediaParams => ({
      // TODO
      customerID: customerID || '',
    }),
    [customerID],
  )

  const allCustomerMedia = c.useSelector((_) =>
    selectAllCustomerMedia(_, selectAllCustomerMediaArgs),
  )

  const [ownerHouseMain] = c.values(allCustomerMedia.owner_house)
  const [ownerHouse = ownerHouseMain] = c.values(
    allCustomerMedia.rc_front_house,
  )

  const ownerHousePropB = React.useMemo((): readonly c.MediaData[] => {
    if (ownerHouse) {
      return [
        {
          type: 'img',
          url: c.getMediaItemSrc(ownerHouse),
        },
      ]
    }
    return c.EMPTY_ARRAY
  }, [ownerHouse])

  const contentUserType = contentUser(homeRep as string)

  const otherAreasPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'problem',
  ) as c.MediaData[]

  const atticPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'insulation',
  ) as c.MediaData[]

  const hvacPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'hvac_system',
  ) as c.MediaData[]
  const thermalPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'thermal',
  ) as c.MediaData[]

  const electricityPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'electricity',
  ) as c.MediaData[]
  const allExteriorWallsPics = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'all_exterior_walls_site_survey',
  ) as c.MediaData[]
  const raftersPics = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rafters_site_survey',
  ) as c.MediaData[]
  const allPlanesRoofPics = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'all_planes_roof_site_survey',
  ) as c.MediaData[]
  const electricalPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'electrical',
  ) as c.MediaData[]
  const electricalOldPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'electrical_panel_site_survey',
  ) as c.MediaData[]
  const otherSolarSurveyPics = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'other_site_survey',
  ) as c.MediaData[]
  const adjustmentPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'adders',
  ) as c.MediaData[]

  const otherDocumentsPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'other_documents',
  ) as c.MediaData[]

  const idPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'id',
  ) as c.MediaData[]

  const roofPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'roof',
  ) as c.MediaData[]

  const solarProposalPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'solar_proposal',
  ) as c.MediaData[]

  const windowsPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'windows',
  ) as c.MediaData[]

  const RCFrontHousePictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_front_house',
  ) as c.MediaData[]

  const RC3DViewPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_3d_view',
  ) as c.MediaData[]

  const RCSolarPanelsPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_solar_panels',
  ) as c.MediaData[]

  const RCAddressVerificationPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_address_verification',
  ) as c.MediaData[]

  const RCDripEdgePictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_drip_edge',
  ) as c.MediaData[]

  const RCFlashingsPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_flashings',
  ) as c.MediaData[]

  const RCGuttersPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_gutters',
  ) as c.MediaData[]

  const RCHailPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_hail',
  ) as c.MediaData[]

  const RCLayersPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_layers',
  ) as c.MediaData[]

  const RCLengthShinglePictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_length_shingle',
  ) as c.MediaData[]

  const RCOtherPropertyPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_other_property_wind_damage',
  ) as c.MediaData[]

  const RCWindDamagesPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_wind_damages',
  ) as c.MediaData[]

  const RCPenetrationsPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_penetrations',
  ) as c.MediaData[]

  const RCInclinationPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_roof_inclination',
  ) as c.MediaData[]

  const RCOverviewPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_roof_overview',
  ) as c.MediaData[]

  const RCShinglesColorPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_shingles_color',
  ) as c.MediaData[]

  const RCGaragePictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_garage',
  ) as c.MediaData[]

  const RCSidingPictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_siding',
  ) as c.MediaData[]

  const RCWiresHomePictures = useGetMediaUrls(
    'images-videos',
    customerID as string,
    'rc_wires_home',
  ) as c.MediaData[]

  const testimonialVideoItems = useGetMediaUrls(
    'mediaItem',
    customerID as string,
    'testimonial_video',
  ) as string[]

  React.useEffect((): c.EmptyFn => {
    c.dispatch(c.subToCustomerMedia({ customerID }))

    return (): void => {
      c.dispatch(
        c.unSubFromCustomerMedia({
          customerID,
        }),
      )
    }
  }, [customerID])

  const toggleSignatureModal = React.useCallback(() => {
    setSignatureModalOpen((current) => !current)
  }, [setSignatureModalOpen])

  const toggleCompanySignatureModal = React.useCallback(() => {
    setCompanySignatureModalOpen((current) => !current)
  }, [setCompanySignatureModalOpen])

  const toggleRepSignatureModal = React.useCallback(() => {
    setRepSignatureModalOpen((current) => !current)
  }, [setRepSignatureModalOpen])

  const settingsIconStyle = React.useMemo(
    () => ({
      color: c.companyColors[solarCompany],
      cursor: 'pointer',
      fontSize: '72px',
    }),
    [solarCompany],
  )

  // React.useEffect(() => {
  //   const res = verifyEmptyFields(customer)

  //   if (res.empty) {
  //     navigate(`/customers/${customerID}#${res.targetScroll}`)
  //   }
  // }, [customer, customerID, navigate])

  React.useEffect(() => {
    if (
      invoiceModal ||
      companySignatureModalOpen ||
      repSignatureModalOpen ||
      signatureModalOpen
    ) {
      document.body.addEventListener('wheel', handleWheelEvent)
      document.body.addEventListener('keydown', handleKeyEvent)
    }
    return () => {
      document.body.removeEventListener('wheel', handleWheelEvent)
      document.body.removeEventListener('keydown', handleKeyEvent)
    }
  }, [
    companySignatureModalOpen,

    invoiceModal,
    repSignatureModalOpen,
    signatureModalOpen,
  ])

  if (!customerID) {
    return null
  }

  const pages = (
    <div ref={signingRoot} className={styles['container']}>
      <>
        {coverShown && (
          <PageReport company={solarCompany} noPDF={summaryNoPDF} vPadDisable>
            <MainPageReport
              customerAddress={customerAddress}
              customerID={customerID}
              customerName={customerName}
              homeRep={homeRep}
              houseViewPic={ownerHouse && c.getMediaItemSrc(ownerHouse)}
              isPDF={pdfLoading}
              readOnly={readOnly}
              solarCompany={solarCompany}
              solarRep={solarRep}
              sortKey={sort_key}
            />
          </PageReport>
        )}

        {inReportScope(currProducts, 'Security', productsResolved) && (
          // Product line-up sheet — Jaron asked for it "above the other
          // comparison you made" (ticket 12925016320), so it renders before
          // SalesSlick. noPDF, same as SalesSlick: the two pages are a pair.
          <ProductLineup company={solarCompany} noPDF />
        )}

        {inReportScope(currProducts, 'Security', productsResolved) &&
          !pdfLoading &&
          comparisonEnabled && (
            // Jaron (13312401562): "I want it to go here", after the line-up
            // and before "Who really owns your…". Off the PDF like both.
            // Switched off globally: not mounted at all, so neither the page
            // nor its "+ Compare" button shows.
            <OtherProviders
              company={solarCompany}
              customerID={customerID}
              readOnly={readOnly}
            />
          )}

        {inReportScope(currProducts, 'Security', productsResolved) && (
          // Marketing comparison graphic — not part of the actual contract
          // (Caballero's Electric flagged it in a downloaded PDF, Slack
          // "This is not part of the contract"). Keep it on the live web
          // view, exclude it from the downloaded/exported PDF.
          <SalesSlick company={solarCompany} noPDF />
        )}

        {/* Jaron, #development-dpt 2026-09-10 ("switch these two"): the Videos
            section moves above the Job Summary block. */}
        {(currProducts.length > 0 || productsResolved) &&
          smartHomeVideosVisible(currProducts, showRoofClaim) && (
            <ProductVideos company={solarCompany} />
          )}

        {showJobSummary &&
          coverShown &&
          (currProducts.length > 0 || productsResolved) && (
            <Summary
              isPDF={pdfLoading}
              noPDF={summaryNoPDF}
              readOnly={readOnly}
              hideSignButtons={hideJobSummarySigns}
              toggleSignNotes={jobSummaryNotesSignToggle}
              toggleSignMonthly={jobSummaryMonthlySignToggle}
              toggleSignPayment={jobSummaryPaymentSignToggle}
            />
          )}

        {/* PPA Stuff */}
        {/* These calculator widgets don't persist to Firebase (local
            component state only) — during public token-signing, blocking
            pointer events is enough to keep them from looking editable. */}
        {contentUserType === 'NORMAL' &&
          c.CODENAME !== 'glow' &&
          c.CODENAME !== 'nirvana' &&
          inReportScope(currProducts, 'Solar', productsResolved) &&
          state !== 'nevada' && (
            <div style={readOnlyCalculatorStyle(readOnly)}>
              <FinancingPage
                alarmBill={altAlarmBill}
                company={solarCompany}
                contentUser={'SOLAR'}
                handleAlarmBill={handleAltAlarmBill}
                handlePowerBill={handleAltPowerBill}
                powerBill={altPowerBill}
                handleSolarBill={handleAltSolarBill}
                solarBill={altSolarBill}
                solarCompany={solarCompany}
                totalFinance={totalCostCompany}
              />
            </div>
          )}

        {/* MAIN FINANCING PAGE, the one with the pie chart */}
        {c.CODENAME !== 'glow' &&
          inReportScope(currProducts, 'Solar', productsResolved) && (
            <div style={readOnlyCalculatorStyle(readOnly)}>
              <FinancingPage
                alarmBill={alarmBill}
                company={solarCompany}
                contentUser={contentUserType}
                handleAlarmBill={handleAlarmBill}
                handlePowerBill={handlePowerBill}
                powerBill={powerBill}
                handleSolarBill={handleSolarBill}
                solarBill={solarBill}
                solarCompany={solarCompany}
                totalFinance={totalCostCompany}
              />
            </div>
          )}

        {inReportScope(currProducts, 'HIL', productsResolved) && (
          <PageReport
            autoHeight
            company={solarCompany}
            companyBorder
            noPDF
            title="Loan Options"
          >
            <div style={readOnlyCalculatorStyle(readOnly)}>
              <LoanOptions />
            </div>
          </PageReport>
        )}

        {/* FAQ  */}
        {contentUserType === 'SOLAR' &&
        sectionSent(signingSections, 'solar') ? (
          <PageReport company={solarCompany} companyBorder title="Faq">
            <FaqPage />
          </PageReport>
        ) : //   </AccordionDetails>
        // </Accordion>
        null}

        {c.CODENAME !== 'glow' &&
          inReportScope(currProducts, 'Solar', productsResolved) &&
          state !== 'nevada' && (
            <PageReport
              company={solarCompany}
              companyBorder
              noPDF
              title="Cumulative Comparison"
            >
              <div style={readOnlyCalculatorStyle(readOnly)}>
                <CumComp />
              </div>
            </PageReport>
          )}

        {/* FINANCING CALCULATOR */}
        {c.CODENAME !== 'glow' &&
          c.CODENAME !== 'nirvana' &&
          inReportScope(currProducts, 'Solar', productsResolved) && (
            <PageReport
              autoHeight
              company={solarCompany}
              companyBorder={false}
              noPDF
            >
              <div style={readOnlyCalculatorStyle(readOnly)}>
                <FinancingCalculator
                  alarmBill={alarmBill}
                  contentUser={contentUserType}
                  energyBill={powerBill}
                  solarBill={solarBill}
                />
              </div>
            </PageReport>
          )}

        {/* Cumulative comparison */}

        {/* The FAQ page was here a second time, character for character. Both
            copies arrived in the same commit (6915ffdd), so this was a paste,
            not two intentions: on any codename that is not `glow` — EcoHome
            included — both conditions held at once and an Alan Ferniza
            customer got two identical Faq pages, on screen and in the PDF.

            This copy is the one removed rather than the earlier one because
            this one carried the extra `CODENAME !== 'glow'` guard. Dropping
            the earlier copy instead would have left `glow` with no FAQ at all,
            turning a duplicate into a missing page. */}

        {/* Signing v2 (phase C, 13213634281): Part 2 start card, at the
            beginning of the contract; startable once Part 1 is signed. */}
        {signingV2 && (
          <div style={signingCardSlotStyle(pageWidth, pdfLoading)}>
            <GuidedSigning
              key={customerID + ':' + contractCompanyShortName + ':contract'}
              legacySigned={legacySigned}
              steps={guidedSteps}
              email={recipient}
              root={signingRoot}
              sendEnabled={AUTO_EMAIL_SIGNED_CONTRACT_ENABLED}
              send={sendFinishedContract}
              adopt={adoptGuidedSignature}
              delivery={delivery.shown}
              companyMissing={companyMissing}
              onStart={markSigningV2}
              part="contract"
            />
          </div>
        )}

        {(showRoofClaim || showAuthorization) && (
          <RoofClaimingContractPages
            renderClaim={showRoofClaim}
            renderAuthorization={showAuthorization}
            autofillCleared={roofAutofillCleared}
            onAutofillClearedChange={handleRoofAutofillCleared}
            company={solarCompany}
            customer={customer}
            customerID={customerID}
            readOnly={readOnly}
            isPDF={pdfLoading}
            onOpenSignatureRoofClaimClient={toggleRoofClaimClientSigning}
            onOpenSignatureRoofClaimRep={toggleRoofClaimRepSigning}
            claimDisplay={roofClaimDisplay}
            authorizationDisplay={authorizationDisplay}
          />
        )}

        {c.IS_MAIN &&
          inReportScope(currProducts, 'Security', productsResolved) &&
          (contractCompanyShortName === 'FE' ? (
            <FirstElectricContract
              customerID={customerID}
              isPDF={pdfLoading}
              toggleSecuritySignatureModalCompany={
                secContractSigningCompanyToggle
              }
              toggleSecuritySignatureModalCustomer={
                secContractSigningCustomerToggle
              }
              toggleCancelSignCustomer={secContractCancelSigningCustomerToggle}
              readOnly={readOnly}
            />
          ) : contractCompanyShortName === 'HC' ? (
            <FirstElectricContract
              customerID={customerID}
              isPDF={pdfLoading}
              toggleSecuritySignatureModalCompany={
                secContractSigningCompanyToggle
              }
              toggleSecuritySignatureModalCustomer={
                secContractSigningCustomerToggle
              }
              toggleCancelSignCustomer={secContractCancelSigningCustomerToggle}
              clauses={homeCamContract}
              readOnly={readOnly}
              showAdditionalPages={false}
            />
          ) : contractCompanyShortName === 'Wildcat' ? (
            <WildcatContract
              customerID={customerID}
              isPDF={pdfLoading}
              toggleSecuritySignatureModalCompany={
                secContractSigningCompanyToggle
              }
              toggleSecuritySignatureModalCustomer={
                secContractSigningCustomerToggle
              }
              readOnly={readOnly}
            />
          ) : contractCompanyShortName === r.OTHER_CASH_SHORT_NAME ? (
            <FirstElectricContract
              customerID={customerID}
              isPDF={pdfLoading}
              toggleSecuritySignatureModalCompany={
                secContractSigningCompanyToggle
              }
              toggleSecuritySignatureModalCustomer={
                secContractSigningCustomerToggle
              }
              toggleCancelSignCustomer={secContractCancelSigningCustomerToggle}
              clauses={homeCamContract}
              readOnly={readOnly}
              showAdditionalPages={false}
            />
          ) : contractCompanyShortName === r.OTHER_FIN_SHORT_NAME ? (
            <FirstElectricContract
              customerID={customerID}
              isPDF={pdfLoading}
              toggleSecuritySignatureModalCompany={
                secContractSigningCompanyToggle
              }
              toggleSecuritySignatureModalCustomer={
                secContractSigningCustomerToggle
              }
              toggleCancelSignCustomer={secContractCancelSigningCustomerToggle}
              clauses={mseContract}
              readOnly={readOnly}
              showAdditionalPages={false}
            />
          ) : null)}

        {/* MS Electric — own dedicated component (2026-08-05), deliberately
            NOT gated by currProducts.includes('Security') like the
            FirstElectricContract branch above. MSE no longer sells CCTV
            (Jaron: "ya no se vende") so many MSE customers have no
            'Security' product at all — the old shared gate silently
            rendered NO contract at all for them, which is almost certainly
            what Fernando/Maria meant by "faltan hojas" (missing pages). */}
        {c.IS_MAIN && contractSent && contractCompanyShortName === 'MSE' && (
          <MSElectricContract
            customerID={customerID}
            isPDF={pdfLoading}
            readOnly={readOnly}
            toggleSignOwner1={homeImprovementSignOwner1Toggle}
            toggleSignOwner2={homeImprovementSignOwner2Toggle}
            toggleSignCompany={homeImprovementSignCompanyToggle}
            toggleCancelSignOwner1={homeImprovementCancelSignOwner1Toggle}
            toggleCancelSignOwner2={homeImprovementCancelSignOwner2Toggle}
            toggleConfirmEquipmentSign={homeImprovementConfirmEquipmentToggle}
            toggleConfirmTotalInitials={
              homeImprovementConfirmTotalInitialsToggle
            }
            toggleFinancingInfoSign={financingInfoSignToggle}
          />
        )}

        {/* Caballero's Electric renderiza el MISMO contrato que M+S desde
            2026-09-09. Jaron, Slack 21:12: "On Caballero Electric, please use
            the same format".

            Por que asi y no portando cambios a HomeImprovementAgreement:
            cuando MSE se separo en su propio componente (be3df503, 4-ago) CE
            quedo congelado en ese estado. Desde entonces MSElectricContract
            recibio 36 commits y HomeImprovementAgreement 4, asi que a CE le
            faltan, entre otros, el Scope of Work que imprime en el PDF, Smart
            Home como categoria con su equipo, la linea que pone el dinero de
            la alarma en el contrato (37cd8e7f), el TOTAL leyendo el monto
            financiado, y el calendario de pagos por porcentajes. Replicar 36
            commits sobre un componente que ya es legacy es trabajo tirado.

            Era ademas la direccion prevista desde el principio — Luis,
            2026-08-04, citado en la cabecera de MSElectricContract.tsx:
            "Caballeros podria depender de MS Electric, no al reves".

            Las clausulas NO se mezclan: el nombre de la empresa entra por
            `company` y las clausulas 20 y 21 lo resuelven por token, asi que
            un contrato de CE nunca nombra a M+S. */}
        {c.IS_MAIN && contractSent && contractCompanyShortName === 'CE' && (
          <MSElectricContract
            company={CE_COMPANY}
            customerID={customerID}
            isPDF={pdfLoading}
            readOnly={readOnly}
            toggleSignOwner1={homeImprovementSignOwner1Toggle}
            toggleSignOwner2={homeImprovementSignOwner2Toggle}
            toggleSignCompany={homeImprovementSignCompanyToggle}
            toggleCancelSignOwner1={homeImprovementCancelSignOwner1Toggle}
            toggleCancelSignOwner2={homeImprovementCancelSignOwner2Toggle}
            toggleConfirmEquipmentSign={homeImprovementConfirmEquipmentToggle}
            toggleConfirmTotalInitials={
              homeImprovementConfirmTotalInitialsToggle
            }
            toggleFinancingInfoSign={financingInfoSignToggle}
          />
        )}

        {/* ESR LLC (Monday 13376550851, Jaron Oct 23): "add this as another
            contractor just like MS Electric. It can say the same thing as MS
            but remove the boxes and just write down that info like we had
            before." Same M+S agreement and clauses, ESR's name by `company`
            (clauses 20/21 resolve it by token), and `writtenCover`: no boxes,
            no input boxes. Its Certificate of Completion is the shared one
            below (read-only lines, company from the Firebase record), not
            MSElectricCertOfCompletion, which hardcodes M+S. */}
        {c.IS_MAIN &&
          contractSent &&
          contractCompanyShortName === ESR_SHORT_NAME && (
            <MSElectricContract
              company={esrCompany}
              customerID={customerID}
              isPDF={pdfLoading}
              readOnly={readOnly}
              toggleSignOwner1={homeImprovementSignOwner1Toggle}
              toggleSignOwner2={homeImprovementSignOwner2Toggle}
              toggleSignCompany={homeImprovementSignCompanyToggle}
              toggleCancelSignOwner1={homeImprovementCancelSignOwner1Toggle}
              toggleCancelSignOwner2={homeImprovementCancelSignOwner2Toggle}
              toggleConfirmEquipmentSign={homeImprovementConfirmEquipmentToggle}
              toggleConfirmTotalInitials={
                homeImprovementConfirmTotalInitialsToggle
              }
              toggleFinancingInfoSign={financingInfoSignToggle}
              writtenCover
            />
          )}

        {/* MSE gets its own dedicated Certificate of Completion (2026-08-06,
            same "own vaina" rule as MSElectricContract.tsx) — the shared
            component below is used by CE/FE/HC too, and MSE-only changes
            (editable Buyer/Address/Phone/Email/Rep fields, no orange
            stripes, "Smart Home" Scope of Work) can't land there without
            affecting them. */}
        {c.IS_MAIN &&
          contractCompanyShortName === 'MSE' &&
          sectionSent(signingSections, 'certificate') &&
          inReportScope(currProducts, 'Security', productsResolved) && (
            <MSElectricCertOfCompletion
              customerID={customerID}
              isPDF={pdfLoading}
              noPDF={certificateNoPDF}
              readOnly={readOnly}
              toggleSecCompletionSignature={toggleSecCompletionSigning}
            />
          )}

        {c.IS_MAIN &&
          // No contract company assigned means contractCompanyFull falls
          // back to unselectedContractCompany (react-utils.tsx), whose
          // fields are the literal string '[[Select a company for the
          // contract]]' - unlike LoanOptions' matching placeholder, nothing
          // here hides it, so it prints for real on this document. Hits any
          // customer whose record predates the company-picker feature
          // (created before Jan 28 2025, so useContractCompany's
          // auto-select-if-one-company effect never ran for them) and who
          // never had contractCompany set by hand. Confirmed on a real
          // customer (Concepcion Carlos, signed 2024-03-27, no Certificate
          // of Completion signature ever recorded - nothing signed is
          // hidden by adding this guard).
          !!contractCompanyShortName &&
          contractCompanyShortName !== 'MSE' &&
          sectionSent(signingSections, 'certificate') &&
          inReportScope(currProducts, 'Security', productsResolved) && (
            <CertOfCompletion
              // CE (Caballero's Electric) has no separate Nevada Disclosure
              // Statement page anymore (Jaron: "cambia esta hojita por el
              // certificado de completacion") — Certificate of Completion
              // IS the CE contract's last page, alarm-safe (T56) since it's
              // now part of the CE contract itself. Corrected 2026-07-28
              // (Luis): the "Include Certificate of Completion" menu toggle
              // must control inclusion for CE too, same as every other
              // company — it was previously forced to always-include
              // (noPDF={false} unconditionally for CE), which left the
              // checkbox visible but silently inert.
              // ESR follows M+S, whose own certificate is alarm-safe too.
              alarmSafe={
                contractCompanyShortName === 'CE' ||
                contractCompanyShortName === ESR_SHORT_NAME
              }
              customerID={customerID}
              isPDF={pdfLoading}
              noPDF={certificateNoPDF}
              readOnly={readOnly}
              toggleSecCompletionSignature={toggleSecCompletionSigning}
            />
          )}

        {/* Ticket 12704562114 (Jaron): explain the Las Vegas alarm verified
            response requirement on the proposal itself, not only as a clause
            inside the contract. Gate is unchanged - Nevada, and only when
            Security is actually in scope for this customer.

            Position: mounted AFTER every contract block and its Certificate of
            Completion (2026-09-07, Jaron follow-up 2026-09-04: "Not when you
            are just downloading the contract"). The downloaded PDF must open on
            the contract's first page, never on this notice - downloadReportAsPdf
            walks [data-pdf-page] in DOM order, so page order == render order
            here. It used to render just before the contract block (~line 815),
            which is what put it at the top of the file. Still rendered
            on-screen in the customer report and still included in the PDF -
            Myriam wants the page kept, it just must not come first. */}
        {c.IS_MAIN &&
          state === 'nevada' &&
          inReportScope(currProducts, 'Security', productsResolved) && (
            <AlarmVerifiedResponsePage solarCompany={solarCompany} />
          )}

        {false && c.IS_MAIN && (
          <PageReport
            autoHeight
            company={solarCompany}
            companyBorder
            title="Service Agreement"
          >
            <ReleaseLiabilityPage
              customerID={customerID as string}
              //
              c_signature={c_signature}
              c_signature_date={c_signature_date}
              //
              reportCompanySignature={report_company_signature}
              reportCompanySignDate={report_company_signature_date}
              //
              solarCompany={solarCompany}
              //
              toggleCompanySignatureModal={toggleCompanySignatureModal}
              toggleSignatureModal={toggleSignatureModal}
            />
          </PageReport>
        )}

        {c.CODENAME === 'nirvana' && (
          <NirvanaContract
            customerID={customerID}
            isPDF={pdfLoading}
            toggleSignatureModal={toggleSignatureModal}
            toggleCompanySignatureModal={toggleCompanySignatureModal}
          />
        )}

        {(!signingSections || showRoofClaim) && (
          <MediaImagePages
            onlyRoofClaim={!!signingSections}
            houseViewPictures={ownerHousePropB}
            // Assessment
            atticPictures={atticPictures}
            roofPictures={roofPictures}
            windowPictures={windowsPictures}
            hvacPictures={hvacPictures}
            otherAreasPictures={otherAreasPictures}
            // Documentation
            idPictures={idPictures}
            otherDocumentsPictures={otherDocumentsPictures}
            // Solar Survey
            thermalPictures={thermalPictures}
            solarProposalPictures={solarProposalPictures}
            electricityPictures={electricityPictures}
            allExteriorWallsPics={allExteriorWallsPics}
            raftersPics={raftersPics}
            allPlanesRoofPics={allPlanesRoofPics}
            electricalPictures={electricalPictures}
            otherSolarSurveyPics={otherSolarSurveyPics}
            adjustmentPictures={adjustmentPictures}
            electricalOldPictures={electricalOldPictures}
            // Roof Claims
            rcFrontHousePictures={RCFrontHousePictures}
            rcAddressVerificationPictures={RCAddressVerificationPictures}
            rc3DViewPictures={RC3DViewPictures}
            rcSolarPanelsPictures={RCSolarPanelsPictures}
            rcGaragePics={RCGaragePictures}
            rcWiresHomePictures={RCWiresHomePictures}
            rcSlidingPictures={RCSidingPictures}
            rcLayersPictures={RCLayersPictures}
            rcInclinationPictures={RCInclinationPictures}
            rcRoofOverviewPictures={RCOverviewPictures}
            rcDripEdgePictures={RCDripEdgePictures}
            rcGuttersPictures={RCGuttersPictures}
            rcFlashingsPictures={RCFlashingsPictures}
            rcPenetrationsPictures={RCPenetrationsPictures}
            rcHailPictures={RCHailPictures}
            rcPropertyDamagesPics={RCOtherPropertyPictures}
            rcWindDamagesPics={RCWindDamagesPictures}
            rcShingleLengthPics={RCLengthShinglePictures}
            rcShinglesColorPics={RCShinglesColorPictures}
            showRoofClaim={showRoofClaim}
            roofClaimExcluded={roofClaimDisplay.noPDF}
            //
          />
        )}

        {!signingSections && (
          <TestimonialVideoPage
            company={solarCompany}
            customerID={customerID}
            existingItemIDs={testimonialVideoItems}
          />
        )}
      </>
    </div>
  )

  return (
    <ThemeProvider theme={reportTheme}>
      <>
        <CssBaseline />
        {(pdfLoading || pdfError || emailLoading || emailError) && (
          <div
            aria-busy={pdfLoading || emailLoading}
            aria-live="assertive"
            className={styles['pdf-splash']}
            role="status"
          >
            <div className={styles['pdf-splash-card']}>
              {pdfLoading ? (
                <>
                  <CircularProgress size={56} />
                  <p className={styles['pdf-splash-title']}>Generating PDF</p>
                  <p className={styles['pdf-splash-message']}>
                    Please wait while we prepare the customer report.
                  </p>
                </>
              ) : pdfError ? (
                <>
                  <p className={styles['pdf-splash-title']}>
                    PDF generation failed
                  </p>
                  <p className={styles['pdf-splash-error']}>
                    {pdfError || 'Unexpected error while generating the PDF.'}
                  </p>
                  <Button onClick={dismissPdfError} variant="contained">
                    Dismiss
                  </Button>
                </>
              ) : emailLoading ? (
                <>
                  <CircularProgress size={56} />
                  <p className={styles['pdf-splash-title']}>Sending email</p>
                  <p className={styles['pdf-splash-message']}>
                    Please wait while we send the agreement.
                  </p>
                </>
              ) : (
                <>
                  <p className={styles['pdf-splash-title']}>
                    Failed to send email
                  </p>
                  <p className={styles['pdf-splash-error']}>
                    {emailError || 'Unexpected error while sending the email.'}
                  </p>
                  <Button onClick={dismissEmailError} variant="contained">
                    Dismiss
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        <div style={signingCardSlotStyle(pageWidth, pdfLoading)}>
          {/* Signing v2 (phase C): this is the Part 1 card (report); Part 2
              sits right before the contract. Not v2: the single card. */}
          <GuidedSigning
            key={customerID + ':' + contractCompanyShortName}
            legacySigned={legacySigned}
            steps={guidedSteps}
            email={recipient}
            root={signingRoot}
            sendEnabled={AUTO_EMAIL_SIGNED_CONTRACT_ENABLED}
            send={sendFinishedContract}
            adopt={adoptGuidedSignature}
            delivery={delivery.shown}
            companyMissing={companyMissing}
            onStart={() => {
              markSigningV2()
              setReportSigningStarted(true)
            }}
            part={signingV2 ? 'report' : undefined}
          />
        </div>
        {pages}
        <cp.Breadcrumbs />
        <div className={styles['menu-pdf']}>
          <SettingsIcon onClick={openSettings} sx={settingsIconStyle} />
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={anchorOrigin}
            aria-labelledby="demo-positioned-button"
            id="demo-positioned-menu"
            onClose={handleClose}
            open={openMenu}
            sx={sx['menuPdfOptions']}
            transformOrigin={menuTransformOrigin}
          >
            {/* Product toggles write to the customer record (which products
                the deal includes) — rep-only, never on the public signing
                view (V50). Same for "Send by email" further down. The
                cover/cert-of-completion toggles and Download PDF are local
                view options, so the customer keeps those. */}
            {!readOnly && c.CODENAME !== 'nirvana' && (
              <MenuItem onClick={toggleSecurity}>
                {currProducts.includes('Security') ? (
                  <Fontisto
                    color={t.backdrop.highlight}
                    name="checkbox-active"
                  />
                ) : (
                  <Fontisto
                    color={t.backdrop.color as string}
                    name="checkbox-passive"
                  />
                )}

                {gs.colGap8}

                <cpm.Text colorScheme="light">Smart Home</cpm.Text>
              </MenuItem>
            )}

            {!readOnly && (
              <MenuItem onClick={toggleSolar}>
                {currProducts.includes('Solar') ? (
                  <Fontisto
                    color={t.backdrop.highlight}
                    name="checkbox-active"
                  />
                ) : (
                  <Fontisto
                    color={t.backdrop.color as string}
                    name="checkbox-passive"
                  />
                )}

                {gs.colGap8}

                <cpm.Text colorScheme="light">Solar</cpm.Text>
              </MenuItem>
            )}
            {!readOnly && (
              <MenuItem onClick={toggleHIL}>
                {currProducts.includes('HIL') ? (
                  <Fontisto
                    color={t.backdrop.highlight}
                    name="checkbox-active"
                  />
                ) : (
                  <Fontisto
                    color={t.backdrop.color as string}
                    name="checkbox-passive"
                  />
                )}

                {gs.colGap8}

                <cpm.Text colorScheme="light">Home Improvements</cpm.Text>
              </MenuItem>
            )}
            {false && c.CODENAME !== 'nirvana' && (
              <MenuItem onClick={toggleRC}>
                {currProducts.includes('RC') ? (
                  <Fontisto
                    color={t.backdrop.highlight}
                    name="checkbox-active"
                  />
                ) : (
                  <Fontisto
                    color={t.backdrop.color as string}
                    name="checkbox-passive"
                  />
                )}

                {gs.colGap8}

                <cpm.Text colorScheme="light">Roof Claims</cpm.Text>
              </MenuItem>
            )}
            {/* Rep-only: a signing customer cannot hide what they must sign. */}
            {!readOnly && showRoofClaim && (
              <MenuItem onClick={toggleIncludeRoofClaim}>
                <Fontisto
                  color={
                    includeRoofClaim
                      ? t.backdrop.highlight
                      : (t.backdrop.color as string)
                  }
                  name={
                    includeRoofClaim ? 'checkbox-active' : 'checkbox-passive'
                  }
                />

                {gs.colGap8}

                <cpm.Text colorScheme="light">Roof Claim</cpm.Text>
              </MenuItem>
            )}
            {!readOnly && showRoofClaim && (
              <MenuItem onClick={toggleIncludeAuthorization}>
                <Fontisto
                  color={
                    includeAuthorization
                      ? t.backdrop.highlight
                      : (t.backdrop.color as string)
                  }
                  name={
                    includeAuthorization
                      ? 'checkbox-active'
                      : 'checkbox-passive'
                  }
                />

                {gs.colGap8}

                <cpm.Text colorScheme="light">Authorization Form</cpm.Text>
              </MenuItem>
            )}
            {!signingSections && (
              <MenuItem onClick={toggleCoverAndSummary}>
                <Fontisto
                  color={
                    includeCoverAndSummary
                      ? t.backdrop.highlight
                      : (t.backdrop.color as string)
                  }
                  name={
                    includeCoverAndSummary
                      ? 'checkbox-active'
                      : 'checkbox-passive'
                  }
                />

                {gs.colGap8}

                <cpm.Text colorScheme="light">
                  Include cover &amp; summary
                </cpm.Text>
              </MenuItem>
            )}
            {!signingSections && (
              <MenuItem onClick={toggleCertOfCompletion}>
                <Fontisto
                  color={
                    includeCertOfCompletion
                      ? t.backdrop.highlight
                      : (t.backdrop.color as string)
                  }
                  name={
                    includeCertOfCompletion
                      ? 'checkbox-active'
                      : 'checkbox-passive'
                  }
                />

                {gs.colGap8}

                <cpm.Text colorScheme="light">
                  Include Certificate of Completion
                </cpm.Text>
              </MenuItem>
            )}
            <MenuItem disabled={pdfLoading} onClick={handleDownloadPdf}>
              {pdfLoading ? (
                <>
                  <CircularProgress size={20} />
                  <span> Generating PDF...</span>
                </>
              ) : (
                <>
                  <Download sx={sx['pdfIcon']} />
                  <span> Download PDF</span>
                </>
              )}
            </MenuItem>

            {!readOnly && (
              <MenuItem
                disabled={emailLoading || !recipient || companyMissing}
                onClick={openSendDialog}
                title={
                  !recipient
                    ? 'No email on file for this customer'
                    : companyMissing
                    ? SELECT_COMPANY_FIRST
                    : undefined
                }
              >
                {emailLoading ? (
                  <>
                    <CircularProgress size={20} />
                    <span> Sending email...</span>
                  </>
                ) : emailSent ? (
                  <>
                    <Email sx={sx['pdfIcon']} />
                    <span> Email sent!</span>
                  </>
                ) : (
                  <>
                    <Email sx={sx['pdfIcon']} />
                    <span>
                      {' '}
                      Send by email{!recipient ? ' (no email)' : ''}
                      {companyMissing ? ' (' + SELECT_COMPANY_FIRST + ')' : ''}
                    </span>
                  </>
                )}
              </MenuItem>
            )}

            {!readOnly && isInvoiceUser && (
              <MenuItem onClick={openInvoiceModal} sx={sx['openInvoice']}>
                <Receipt sx={sx['pdfIcon']} /> Open invoice
              </MenuItem>
            )}
          </Menu>
        </div>
        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={closeSendDialog}
          open={!!sendSections}
        >
          <DialogTitle>Send signing link</DialogTitle>
          <DialogContent>
            <p style={sx.sendTo}>
              To: <strong>{recipient}</strong>
            </p>
            <p style={sx.sendTo}>The customer will only see:</p>
            {sendChoice.available.map((section) => (
              <div key={section}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!sendSections?.includes(section)}
                      onChange={() => toggleSendSection(section)}
                    />
                  }
                  label={SIGNING_SECTION_LABELS[section]}
                />
              </div>
            ))}
            {!!sendBlock && (
              <p role="alert" style={sx.sendBlock}>
                {sendBlock}
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSendDialog}>Cancel</Button>
            <Button
              disabled={!!sendBlock}
              onClick={handleSendEmail}
              title={sendBlock || undefined}
              variant="contained"
            >
              Send link
            </Button>
          </DialogActions>
        </Dialog>
        <Modal
          open={hilCompletionSigning}
          onRequestClose={toggleHILCompletionSigning}
        >
          <Signature
            customerID={customerID}
            field="hil_completion_client_sign"
            onRequestClose={toggleHILCompletionSigning}
          />
        </Modal>
        <Modal
          open={secCompletionSigning}
          onRequestClose={toggleSecCompletionSigning}
        >
          <Signature
            customerID={customerID}
            field="security_completion_sign_customer"
            onRequestClose={toggleSecCompletionSigning}
          />
        </Modal>
        <Modal
          open={secContractSigningCompanyOpen}
          onRequestClose={secContractSigningCompanyToggle}
        >
          <Signature
            customerID={customerID}
            field="security_contract_sign_company"
            onRequestClose={secContractSigningCompanyToggle}
          />
        </Modal>
        <Modal
          open={secContractSigningCustomerOpen}
          onRequestClose={secContractSigningCustomerToggle}
        >
          <Signature
            customerID={customerID}
            field="security_contract_sign_customer"
            onRequestClose={secContractSigningCustomerToggle}
          />
        </Modal>
        <Modal
          open={secContractCancelSigningCustomerOpen}
          onRequestClose={secContractCancelSigningCustomerToggle}
        >
          <Signature
            customerID={customerID}
            field="security_contract_cancel_sign_customer"
            onRequestClose={secContractCancelSigningCustomerToggle}
          />
        </Modal>
        <Modal
          open={homeImprovementSignOwner1Open}
          onRequestClose={homeImprovementSignOwner1Toggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_sign_owner1"
            onRequestClose={homeImprovementSignOwner1Toggle}
          />
        </Modal>
        <Modal
          open={homeImprovementSignOwner2Open}
          onRequestClose={homeImprovementSignOwner2Toggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_sign_owner2"
            onRequestClose={homeImprovementSignOwner2Toggle}
          />
        </Modal>
        <Modal
          open={homeImprovementSignCompanyOpen}
          onRequestClose={homeImprovementSignCompanyToggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_sign_company"
            onRequestClose={homeImprovementSignCompanyToggle}
          />
        </Modal>
        <Modal
          open={homeImprovementCancelSignOwner1Open}
          onRequestClose={homeImprovementCancelSignOwner1Toggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_cancel_sign_owner1"
            onRequestClose={homeImprovementCancelSignOwner1Toggle}
          />
        </Modal>
        <Modal
          open={homeImprovementCancelSignOwner2Open}
          onRequestClose={homeImprovementCancelSignOwner2Toggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_cancel_sign_owner2"
            onRequestClose={homeImprovementCancelSignOwner2Toggle}
          />
        </Modal>
        <Modal
          open={homeImprovementConfirmEquipmentOpen}
          onRequestClose={homeImprovementConfirmEquipmentToggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_confirm_equipment_sign"
            onRequestClose={homeImprovementConfirmEquipmentToggle}
          />
        </Modal>
        <Modal
          open={homeImprovementConfirmTotalInitialsOpen}
          onRequestClose={homeImprovementConfirmTotalInitialsToggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_confirm_total_initials"
            onRequestClose={homeImprovementConfirmTotalInitialsToggle}
          />
        </Modal>
        <Modal
          open={financingInfoSignOpen}
          onRequestClose={financingInfoSignToggle}
        >
          <Signature
            customerID={customerID}
            field="home_improvement_financing_info_sign"
            onRequestClose={financingInfoSignToggle}
          />
        </Modal>
        <Modal
          open={jobSummaryPaymentSignOpen}
          onRequestClose={jobSummaryPaymentSignToggle}
        >
          <Signature
            customerID={customerID}
            extraOnSave={paymentSignExtras}
            field="job_summary_payment_sign"
            onRequestClose={jobSummaryPaymentSignToggle}
          />
        </Modal>
        <Modal
          open={jobSummaryMonthlySignOpen}
          onRequestClose={jobSummaryMonthlySignToggle}
        >
          <Signature
            customerID={customerID}
            field="job_summary_monthly_sign"
            onRequestClose={jobSummaryMonthlySignToggle}
          />
        </Modal>
        <Modal
          open={jobSummaryNotesSignOpen}
          onRequestClose={jobSummaryNotesSignToggle}
        >
          <Signature
            customerID={customerID}
            field="job_summary_notes_sign"
            onRequestClose={jobSummaryNotesSignToggle}
          />
        </Modal>
        <Modal
          onRequestClose={toggleRoofClaimClientSigning}
          open={roofClaimClientSigning}
        >
          <Signature
            customerID={customerID}
            field="roof_claiming_client_sign"
            extraOnSave={() => ({
              ...roofClaimStamp(customer),
              ...roofClaimAutofillPatch(customer, roofAutofillCleared),
            })}
            onRequestClose={toggleRoofClaimClientSigning}
          />
        </Modal>
        <Modal
          onRequestClose={toggleRoofClaimRepSigning}
          open={roofClaimRepSigning}
        >
          <Signature
            customerID={customerID}
            field="roof_claiming_company_rep_sign"
            extraOnSave={() => ({
              ...roofClaimStamp(customer),
              ...roofClaimAutofillPatch(customer, roofAutofillCleared),
            })}
            onRequestClose={toggleRoofClaimRepSigning}
          />
        </Modal>
        <Modal open={signatureModalOpen} onRequestClose={toggleSignatureModal}>
          <Signature
            customerID={customerID}
            field="c_signature"
            onRequestClose={toggleSignatureModal}
          />
        </Modal>
        <Modal
          open={companySignatureModalOpen}
          onRequestClose={toggleCompanySignatureModal}
        >
          <Signature
            customerID={customerID}
            field="report_company_signature"
            onRequestClose={toggleCompanySignatureModal}
          />
        </Modal>
        <Modal
          open={repSignatureModalOpen}
          onRequestClose={toggleRepSignatureModal}
        >
          <Signature
            customerID={customerID}
            field="r_signature"
            onRequestClose={toggleRepSignatureModal}
          />
        </Modal>
        <ToastContainer
          position="top-left"
          autoClose={1300}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </>
    </ThemeProvider>
  )
})

export const dateToNumber = (date: string) => {
  if (date !== 'mm/dd/yyyy') {
    return Date.parse(date).toString()
  } else {
    return '0'
  }
}

const anchorOrigin = {
  horizontal: 'left',
  vertical: 'top',
} as const

const menuTransformOrigin = {
  horizontal: 'right',
  vertical: 'top',
} as const

export interface ConsolidationDebtDataExtend extends c.ConsolidationDebtData {
  address: string
  closer: string
  creditScore: string
  notes: string
}

export const rootSurfaceCustomerReport: c.Surface = 'pasteboard'
const sx = {
  menuPdfOptions: {
    marginRight: '20px',
  },
  sendBlock: {
    color: '#c62828',
    margin: '12px 0 0',
  },
  sendTo: {
    margin: '0 0 8px',
  },
  openInvoice: {
    alignItems: 'center',
    color: '#2626f1',
    display: 'flex',
    textAlign: 'center',
    textDecoration: 'underline',
    width: '100%',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  pdfIcon: {
    height: '20px',
    width: '20px',
  },
  settingsIcon: {
    color: 'pink',
    cursor: 'pointer',
    fontSize: '72px',
  },
}

function readOnlyCalculatorStyle(
  readOnly: boolean,
): React.CSSProperties | undefined {
  return readOnly ? { opacity: 0.6, pointerEvents: 'none' } : undefined
}

const handleWheelEvent = (e: any) => {
  e.preventDefault()
}

const handleKeyEvent = (e: any) => {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    e.preventDefault()
  }
}
